import { ListItemButton, type ListItemButtonProps, ListItemIcon, type ListItemIconProps, ListItemText, Typography, useMediaQuery } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useEffect } from 'react';
import { Link, useLocation } from 'react-router';
import { useAppDispatch, useAppSelector } from '~/application/hooks';
import { selectIsDrawerOpen, setActiveMenuItem, setDrawerOpen } from '~/application/slice';
import type { LinkTarget, NavigationItemType } from '~/types/navigation';

type MainMenuItemProps = ListItemButtonProps & {
    item: NavigationItemType;
    level: number;
};

export const MainMenuItem = ({ item, level, ...props }: MainMenuItemProps) => {
    const dispatch = useAppDispatch();
    const { pathname } = useLocation();

    const drawerOpen = useAppSelector(selectIsDrawerOpen);

    const downLG = useMediaQuery(theme => theme.breakpoints.down('lg'));
    const isSelected = pathname.startsWith(item.url!);

    const Icon = item.icon!;
    const itemIcon = item.icon ? <Icon style={{ fontSize: drawerOpen ? '1rem' : '1.25rem' }} /> : false;

    useEffect(() => {
        if (pathname === item.url) {
            dispatch(setActiveMenuItem([item.id]));
        }
    }, [pathname, item.id, item.url]);

    let itemTarget: LinkTarget = '_self';
    if (item.target) {
        itemTarget = '_blank';
    }

    return (
        <MainMenuItemRoot
            {...props}
            level={level}
            disabled={item.disabled}
            open={drawerOpen}
            selected={isSelected}
            {...(downLG && {
                onClick: () => dispatch(setDrawerOpen(false)),
            })}
            {...(item.url && !item.external && {
                component: Link,
                to: item.url,
                target: itemTarget,
            })}
            {...(item.url && item.external && {
                component: 'a',
                href: item.url,
                target: itemTarget,
            })}
        >
            {itemIcon && (
                <MainMenuItemIcon open={drawerOpen} selected={isSelected}>
                    {itemIcon}
                </MainMenuItemIcon>
            )}
            {(drawerOpen || (!drawerOpen && level !== 1)) && (
                <ListItemText
                    primary={
                        <Typography variant="h6" sx={{ color: isSelected ? 'primary.main' : 'text.primary ' }}>
                            {item.title}
                        </Typography>
                    }
                />
            )}
        </MainMenuItemRoot>
    );
};

const MainMenuItemIcon = styled(ListItemIcon, {
    shouldForwardProp: prop => prop !== 'open' && prop !== 'selected',
})<ListItemIconProps & { open: boolean; selected: boolean }>(
    ({ open, selected, theme }) => ({
        minWidth: 28,
        color: selected ? theme.palette.primary.main : theme.palette.text.primary,
        ...(!open && {
            borderRadius: theme.spacing(1.5),
            width: 36,
            height: 36,
            alignItems: 'center',
            justifyContent: 'center',
            '&:hover': { backgroundColor: theme.palette.secondary.lighter },
        }),
        ...(!open && selected && {
            backgroundColor: theme.palette.secondary.lighter,
            '&:hover': { backgroundColor: theme.palette.primary.lighter },
        }),
    })
);

const MainMenuItemRoot = styled(ListItemButton, {
    shouldForwardProp: prop => prop !== 'level' && prop !== 'open',
})<ListItemButtonProps & { level: number; open: boolean }>(
    ({ level, open, theme }) => ({
        zIndex: theme.zIndex.drawer + 1,
        paddingLeft: open ? `${level * 28}px` : theme.spacing(1.5),
        paddingTop: !open && level === 1 ? theme.spacing(1.25) : theme.spacing(1),
        paddingBottom: !open && level === 1 ? theme.spacing(1.25) : theme.spacing(1),
        ...(open && {
            '&:hover': { backgroundColor: theme.palette.primary.lighter },
            '&.Mui-selected': {
                backgroundColor: theme.palette.primary.lighter,
                borderRight: `2px solid ${theme.palette.primary.main}`,
                color: theme.palette.primary.main,
                '&:hover': {
                    color: theme.palette.primary.main,
                    backgroundColor: theme.palette.primary.lighter,
                },
            },
        }),
        ...(!open && {
            '&:hover': { backgroundColor: 'transparent' },
            '&.Mui-selected': {
                backgroundColor: 'transparent',
                '&:hover': { backgroundColor: 'transparent' },
            }
        }),
    })
);
